import { __extends } from "tslib";
import cx from 'classnames';
import { includes, noop } from 'lodash-es';
import PropTypes from 'prop-types';
import React, { createRef } from 'react';
import { IMAGE_UPLOAD_DONE, IMAGE_UPLOAD_ERROR, IMAGE_UPLOAD_PENDING, } from '../constants/contentStatus';
import * as screenLocation from '../constants/screenLocation';
import { CURRENT_SCREEN_ID, PREVIOUS_SCREEN_ID } from '../constants/userTest';
import BottomBackground from '../containers/BottomBackground';
import FixedHotspotLayer from '../containers/FixedHotspotLayer';
import Footer from '../containers/Footer';
import Header from '../containers/Header';
import HoverOutLayer from '../containers/HoverOutLayer';
import LoadingSpinner from '../containers/LoadingSpinner';
import Overlay from '../containers/Overlay';
import OverlayBackground from '../containers/OverlayBackground';
import ScreenImage from '../containers/ScreenImage';
import ScrollableArea from '../containers/ScrollableArea';
import TopBackground from '../containers/TopBackground';
import { ImageDownloadError, ImageUploadFailedError, ImageUploadPendingError, } from './ImageError';
import styles from './Screen.module.css';
var NO_ANIMATION = [
    'fade-previous',
    'fade-reverse-current',
    'slidedown-current',
    'slidedown-reverse-previous',
    'slideup-previous',
    'slideup-reverse-current',
];
var Screen = /** @class */ (function (_super) {
    __extends(Screen, _super);
    function Screen(props) {
        var _this = _super.call(this, props) || this;
        _this.animatedElement = createRef();
        return _this;
    }
    Screen.prototype.getChildContext = function () {
        return { isCurrentScreen: this.props.isCurrent };
    };
    Screen.prototype.componentDidMount = function () {
        if (!this.hasAnimation()) {
            this.props.onAnimationComplete();
        }
    };
    Screen.prototype.componentDidUpdate = function () {
        if (!this.hasAnimation() && !this.props.isTransitionComplete) {
            // make sure we only fire animation complete once after a screen transition
            this.props.onAnimationComplete();
        }
    };
    Screen.prototype.hasAnimation = function () {
        return (this.props.transition && !includes(NO_ANIMATION, this.getAnimationClass()));
    };
    Screen.prototype.getAnimationClass = function () {
        if (this.props.isScreenTransitionComplete) {
            return null;
        }
        var _a = this.props, isCurrent = _a.isCurrent, transition = _a.transition, isTransitionReversed = _a.isTransitionReversed;
        var screenName = isCurrent ? 'current' : 'previous';
        var animation = [transition, screenName];
        if (isTransitionReversed) {
            animation.splice(1, 0, 'reverse');
        }
        return animation.join('-');
    };
    Screen.prototype.render = function () {
        var _a;
        var _this = this;
        var _b = this.props, id = _b.id, isPlacingAnnotation = _b.isPlacingAnnotation, transition = _b.transition, onClick = _b.onClick, onAnimationComplete = _b.onAnimationComplete;
        if (!id) {
            return null;
        }
        var animationClass = this.getAnimationClass();
        var className = cx(styles.screenWrap, (_a = {},
            _a[styles[transition]] = styles[transition],
            _a[styles[animationClass]] = styles[animationClass],
            _a[styles.screenWrapPlacingAnnotation] = isPlacingAnnotation,
            _a));
        var screen = (React.createElement("div", { className: className, "data-layer-type": "screen", onAnimationEnd: function (e) {
                // When experimenting with overlay-scrollbars we found this was being
                // incorrectly triggered due to scrollbar elements animating and
                // animation events bubbling. Didn't go with overlay-scrollbars in the
                // end but decided to keep this check in case any elements container
                // within Screen have animations.
                if (e.target !== _this.animatedElement.current)
                    return;
                onAnimationComplete(e);
            }, onClick: function (e) {
                return onClick(id, screenLocation.BODY, e, _this.props.getScrollPos());
            }, ref: this.animatedElement }, this._renderScreen()));
        return screen;
    };
    Screen.prototype._renderScreen = function () {
        var _a = this.props, contentStatus = _a.contentStatus, isLoaded = _a.isLoaded, isDownloadFailed = _a.isDownloadFailed, fixBounce = _a.fixBounce, withRef = _a.withRef, getScrollPos = _a.getScrollPos, id = _a.id, onClick = _a.onClick, backgroundColor = _a.backgroundColor, isCurrent = _a.isCurrent;
        if (contentStatus === IMAGE_UPLOAD_ERROR) {
            return React.createElement(ImageUploadFailedError, null);
        }
        if (contentStatus === IMAGE_UPLOAD_PENDING) {
            return React.createElement(ImageUploadPendingError, null);
        }
        if (isDownloadFailed) {
            return React.createElement(ImageDownloadError, null);
        }
        if (!isLoaded) {
            return React.createElement(LoadingSpinner, null);
        }
        return (React.createElement(HoverOutLayer, { getScreenScrollPos: getScrollPos },
            fixBounce && React.createElement(TopBackground, null),
            fixBounce && React.createElement(BottomBackground, null),
            React.createElement(ScrollableArea, { id: this.props.isCurrent ? CURRENT_SCREEN_ID : PREVIOUS_SCREEN_ID, screenID: this.props.id, withRef: withRef },
                React.createElement(ScreenImage, { backgroundColor: backgroundColor, getScreenScrollPos: getScrollPos, screenID: id })),
            React.createElement(Header, { getScreenScrollPos: getScrollPos, onClick: function (e) { return onClick(id, screenLocation.HEADER, e); }, screenID: id }),
            React.createElement(Footer, { getScreenScrollPos: getScrollPos, onClick: function (e) { return onClick(id, screenLocation.FOOTER, e); }, screenID: id }),
            React.createElement(OverlayBackground, null),
            React.createElement(Overlay, { getScreenScrollPos: getScrollPos }),
            React.createElement(FixedHotspotLayer, { getScreenScrollPos: getScrollPos, isCurrentScreen: isCurrent, screenID: id, scrollableAreaRef: withRef })));
    };
    Screen.childContextTypes = {
        isCurrentScreen: PropTypes.bool.isRequired,
    };
    return Screen;
}(React.Component));
export default Screen;
Screen.propTypes = {
    id: PropTypes.number,
    isLoaded: PropTypes.bool.isRequired,
    isDownloadFailed: PropTypes.bool.isRequired,
    contentStatus: PropTypes.oneOf([
        IMAGE_UPLOAD_PENDING,
        IMAGE_UPLOAD_ERROR,
        IMAGE_UPLOAD_DONE,
    ]),
    isScreenTransitionComplete: PropTypes.bool,
    isTransitionReversed: PropTypes.bool,
    isPlacingAnnotation: PropTypes.bool.isRequired,
    transition: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    onAnimationComplete: PropTypes.func,
    isCurrent: PropTypes.bool,
    fixBounce: PropTypes.bool,
    withRef: PropTypes.object,
    getScrollPos: PropTypes.func,
    backgroundColor: PropTypes.string,
};
Screen.defaultProps = {
    onClick: noop,
    onAnimationComplete: noop,
    isCurrent: false,
    isLoaded: false,
    isDownloadFailed: false,
    isTransitionReversed: false,
    isPlacingAnnotation: false,
};
